import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AppSettings} from '../../config/app-setting';
import {LoginResult} from '../entities/login-result';
import {LoginChangePasswordModel} from '../entities/login-changepassword-model';
import {UserAccessPermissions} from "../entities/UserAccessPermissions";
import {GetUserNameResult} from "../entities/GetUserNameResult";
import {CreatedResult} from "../entities/CreatedResult";
import {LoginModel} from "../entities/login-model";
import {GetPermissionResult} from "../entities/GetPermissionResult";
import {UtilityService} from "./utility.service";
import { BaseResult } from '../entities/BaseResult';

@Injectable({
  providedIn: 'root'
})
export class SecurityApiService {

  constructor(private http: HttpClient,
              private utilityService: UtilityService) { }

  async PostChangeUserAccessPermission(accessPermission: UserAccessPermissions): Promise<CreatedResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiUserAccessPermissions`, {
        method: 'POST',
        headers: this.utilityService.GetFetchHttpHeader().headers,
        body: JSON.stringify(accessPermission)
      });

      if (!response.ok) {
        // Gestione errori HTTP
        return {
          success: false,
          error: true,
          errorMessage: 'HTTP error occurred',
        };
      }

      const res: CreatedResult = await response.json();

      if (res.success && !res.error) {
        return res;
      } else {
        return {
          success: false,
          error: true,
          errorMessage: res.errorMessage || 'Unknown error occurred',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: true,
        errorMessage: 'HTTP error occurred',
      };
    }
  }

  async PostLogin(email: string, password: string): Promise<LoginResult> {
    const loginModel: LoginModel = { email, password };

    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiLogin`, {
        method: 'POST',
        headers: this.utilityService.GetFetchHttpHeader().headers,
        body: JSON.stringify(loginModel),
      });

      if (!response.ok) {
        return new LoginResult({
          error: true,
          errorMessage: 'HTTP error occurred'
        });
      }

      const res: LoginResult = await response.json();

      if (res.success && !res.error) {
        return res;
      } else {
        return new LoginResult({
          error: true,
          errorMessage: res.errorMessage || 'Unknown error occurred'
        });
      }
    } catch (error) {
      return new LoginResult({
        error: true,
        errorMessage: 'HTTP error occurred'
      });
    }
  }

  async PostRefreshToken(): Promise<LoginResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/RefreshToken`, {
        method: 'POST',
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        return new LoginResult({
          error: true,
          errorMessage: 'HTTP error occurred'
        });
      }

      const res: LoginResult = await response.json();

      if (res.success && !res.error) {
        return res;
      } else {
        return new LoginResult({
          error: true,
          errorMessage: res.errorMessage || 'Unknown error occurred'
        });
      }
    } catch (error) {
      return new LoginResult({
        error: true,
        errorMessage: 'HTTP error occurred'
      });
    }
  }

  async PostLoginChangePassword(email: string, oldPassword: string, newPassword: string): Promise<LoginResult> {
    try {
      let loginChangePasswordModel: LoginChangePasswordModel;
      loginChangePasswordModel = {email, oldPassword, newPassword};
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiLoginChangePassword`, {
        method: 'POST',
        body: JSON.stringify(loginChangePasswordModel),
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        throw new Error('Errore nella chiamata HTTP');
      }

      const data: LoginResult = await response.json();
      return data;
    } catch (error) {
      return new LoginResult({
        error: true,
        errorMessage: 'Accesso negato. Effettua l\'accesso corretto.'
      });
    }
  }

  async PostResetPassword(email: string, language2LettersCode: string): Promise<LoginResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ResetPassword`, {
        method: 'POST',
        body: JSON.stringify({ "Email": email, "Language2LetterCode": language2LettersCode }),
        headers: this.utilityService.GetFetchHttpHeader().headers
      });
      if (!response.ok) {
        return new LoginResult({
          error: true,
          errorMessage: `Errore durante il reset della password: ${response.statusText}`,
          statusCode : response.status
        });
      }

      const result: LoginResult = await response.json();
      return result;

    } catch (error) {
      return new LoginResult({
        error: true,
        errorMessage: 'Errore durante il reset della password.'
      });
    }
  }

  async PutDisableAccount(email: string): Promise<BaseResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiUserEnabled`, {
        method: 'PUT',
        body: JSON.stringify({ "userName": email, isEnabled: false }),
        headers: this.utilityService.GetFetchHttpHeader().headers
      });
      
      if (!response.ok) {
        return new BaseResult({
          error: true,
          errorMessage: `Errore durante la disattivazione dell'account: ${response.statusText}`,
          statusCode : response.status
        });
      }

      return new BaseResult({
        success: true
      });

    } catch (error) {
      return new BaseResult({
        error: true,
        errorMessage: "Errore durante la disattivazione dell'account."
      });
    }
  }

  async GetApiUserAccessPermissions(): Promise<GetPermissionResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiUserAccessPermissions`, {
        method: 'GET',
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        let res = new GetPermissionResult();
        res.success = false;
        res.errorMessage = 'Errore nella chiamata al server.';
        res.permissionList = [];
        return res;
      }

      const data: GetPermissionResult = await response.json();

      return data;
    } catch (error) {
      let result = new GetPermissionResult();
      result.success = false;
      result.errorMessage = 'Accesso negato. Effettua l\'accesso corretto.';
      result.permissionList = [];
       return result;
    }
  }

  async GetAllUserPermissions(): Promise<GetPermissionResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiUserAccessPermissions/GetAll`, {
        method: 'GET',
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        throw new Error('Errore nella chiamata HTTP');
      }

      const data: GetPermissionResult = await response.json();
      return data;
    } catch (error) {
      let result = new GetPermissionResult();
      result.success = false;
      result.errorMessage = 'Accesso negato. Effettua l\'accesso corretto.';
      result.permissionList = [];
      return result;
    }
  }

  async GetUserName(): Promise<GetUserNameResult> {
    try {
      const response = await fetch(`${AppSettings.securityApiUrl}/ApiUserName`, {
        method: 'GET',
        headers: this.utilityService.GetFetchHttpHeader().headers
      });

      if (!response.ok) {
        let result = new GetUserNameResult();
        result.success = false;
        result.errorMessage = 'Errore nella chiamata al server.';
        result.userName = '';
        return result;
      }

      const data: GetUserNameResult = await response.json();
      return data;
    } catch (error) {
      let result = new GetUserNameResult();
      result.success = false;
      result.errorMessage = 'Accesso negato. Effettua l\'accesso corretto.';
      result.userName = '';
      return result;
    }
  }

}
